export type UserRole = 'viewer' | 'editor' | 'admin' | 'super_admin';

/** Ordered low → high; index = privilege level (see rbac.ts). */
export const ROLE_ORDER: UserRole[] = ['viewer', 'editor', 'admin', 'super_admin'];

/** An application login account (NOT a member). Shape returned by the
 *  super-admin user-management API. */
export interface AppUser {
  id: number;
  email: string | null;
  name: string;
  role: UserRole;
  authMethod: 'entra_sso' | 'manual';
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface Member {
  co_id: number;
  first_name: string | null;
  last_name: string | null;
  aka: string | null;
  sex: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  unlisted_phone: string | null;
  cell: string | null;
  pager: string | null;
  email: string | null;
  dob: string | null;
  status: string | null;
  joined: string | null;
  reactive: string | null;
  inactive: string | null;
  resigned: string | null;
  drop_date: string | null;
  reason_inactive: string | null;
  battalion: string | null;
  card_no: number | null;
  source: string | null;
  area_interest: string | null;
  comments: string | null;
  fingerprint: string | null;
  chest_xray: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/** Trimmed member shape for list/search results. */
export interface MemberListItem {
  co_id: number;
  last_name: string | null;
  first_name: string | null;
  status: string | null;
  battalion: string | null;
  email: string | null;
  phone: string | null;
  cell: string | null;
  joined: string | null;
  is_current: boolean;
}

export interface EmergencyContact {
  id: number;
  co_id: number;
  name: string | null;
  relation: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
}

export interface Physical {
  id: number;
  co_id: number;
  date_of_physical: string | null;
  type: string | null;
  fingerprint_date: string | null;
}

export interface PriorService {
  id: number;
  co_id: number;
  start_date: string | null;
  end_date: string | null;
  reason: string | null;
  total: string | null;
}

export interface EarnedPin {
  id: number;
  co_id: number;
  pin: string | null;
  date_given: string | null;
}

export interface MemberCertification {
  id: number;
  co_id: number;
  cert_type: string;
  cert_level: string | null;
  value: string | null;
  completed_date: string | null;
  expires_date: string | null;
  due_date: string | null;
  notes: string | null;
  source: string | null;
}

export interface Assignment {
  id: number;
  co_id: number;
  gear_issued: string | null;
  battalion: string | null;
  rank: string | null;
  apparatus_operator: string | null;
  suppression_pos: string | null;
}

export interface ServiceHours {
  id: number;
  co_id: number;
  year: number;
  total_hours: number | null;
  source: string | null;
  needs_validation: boolean;
}

export interface MemberStatus {
  code: string;
  is_current: boolean;
  sort_order: number;
}

/**
 * Writable member fields (create/update payload). co_id is required only on
 * create. All values may be sent as strings — the worker trims, coerces
 * numeric columns, and maps "" → null. Audit columns are never writable.
 */
export type MemberInput = Partial<Omit<Member, 'co_id' | 'created_at' | 'updated_at' | 'deleted_at'>> & {
  co_id?: number;
};
